/**
 * usePermissions hook
 * Checks the current user's resolved permissions (from their permission role)
 * to determine what they can see and do in the system.
 * 
 * Permission values: 'read_write', 'read_only', 'no_access'
 */
import { useAuth } from '../contexts/AuthContext';

export function usePermissions() {
  const { user } = useAuth();
  const perms = user?.resolved_permissions || {};
  const isAdmin = user?.is_admin || false;

  /**
   * Check if user can access a resource
   * @param {string} resource - e.g. 'items', 'supplier', 'category_code', 'fn_inventory'
   * @returns {'read_write'|'read_only'|'no_access'}
   */
  const getAccess = (resource) => {
    if (isAdmin) return 'read_write'; // Admins always have full access
    return perms[resource] || 'no_access';
  };

  const canRead = (resource) => {
    const access = getAccess(resource);
    return access === 'read_write' || access === 'read_only';
  };

  const canWrite = (resource) => {
    return getAccess(resource) === 'read_write';
  };

  const canAccess = (resource) => canRead(resource);

  return { getAccess, canRead, canWrite, canAccess, isAdmin, permissions: perms };
}
